import { useEffect, useContext, useState, useRef } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { AuthContext } from '../context/AuthContext'
import { getBackendConfig } from '../config/oauthConfig'

export default function OAuth2Callback() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const { login } = useContext(AuthContext)
  const [error, setError] = useState(null)
  const [isProcessing, setIsProcessing] = useState(true)
  const hasProcessed = useRef(false)

  useEffect(() => {
    // Prevent duplicate processing in React 18 Strict Mode
    if (hasProcessed.current) return
    hasProcessed.current = true

    const handleCallback = async () => {
      try {
        // Get authorization code from URL
        const code = searchParams.get('code')
        const state = searchParams.get('state')
        const storedState = sessionStorage.getItem('oauth_state')

        // Verify state for CSRF protection
        if (!state || state !== storedState) {
          throw new Error('無効なstateパラメータ - CSRF攻撃の可能性があります')
        }

        if (!code) {
          const error = searchParams.get('error')
          const errorDescription = searchParams.get('error_description')
          if (error === 'access_denied') {
            throw new Error('')
          }
          throw new Error(
            errorDescription ||
              error ||
              'Googleから認証コードが受信されませんでした'
          )
        }

        // Exchange code with backend
        const backendConfig = getBackendConfig()
        const response = await fetch(
          `${backendConfig.baseUrl}${backendConfig.googleLoginEndpoint}`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ code })
          }
        )

        const data = await response.json()

        if (!response.ok) {
          throw new Error(
            data.detail || data.message || 'Googleでのログインに失敗しました'
          )
        }

        // Check if user requires approval
        if (data.requires_approval) {
          // User is pending approval
          if (data.status === 'pending_approval') {
            sessionStorage.setItem('pendingApprovalEmail', data.email)
            navigate('/pending-approval', {
              state: { email: data.email, userId: data.user_id }
            })
          } else {
            throw new Error(
              data.message || 'アカウントがまだ承認されていません'
            )
          }
        } else {
          login(
            data.access_token,
            data.role,
            data.user_name,
            data.refresh_token,
            data.user_id
          )

          sessionStorage.removeItem('oauth_state')
          navigate('/home/schedule', { replace: true })
        }
      } catch (err) {
        console.error('OAuthコールバックエラー:', err)
        setError(
          err.message.startsWith('Your account is deactivated.')
            ? 'アカウントが無効になっています。詳細は管理者にお問い合わせください。'
            : err.message
        )
        setIsProcessing(false)
      }
    }

    handleCallback()
  }, [searchParams, navigate, login])

  return (
    <div className='min-h-screen bg-gray-100 flex items-center justify-center w-full p-6'>
      <div className='w-96 text-center'>
        {isProcessing ? (
          <>
            <div className='mb-4'>
              <div className='inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600'></div>
            </div>
            <h2 className='text-xl font-medium text-gray-900 mb-2'>
              ログインを処理中...
            </h2>
            <p className='text-gray-600'>
              Googleで認証中ですので、しばらくお待ちください
            </p>
          </>
        ) : (
          <>
            <h2 className='text-xl font-medium text-red-600 mb-4'>
              認証エラー
            </h2>
            {error && <p className='text-gray-600'>{error}</p>}
            <button
              onClick={() => navigate('/login')}
              className='mt-6 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700'
            >
              ログイン画面に戻る
            </button>
          </>
        )}
      </div>
    </div>
  )
}
